import { useEffect, useState } from "react";
import { ArrowUp } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Floating "back to top" button. Appears once the page (or the app's inner
 * scroll container) has been scrolled down, and smooth-scrolls back to the top.
 */
const THRESHOLD = 400;

function getScrollContainer(): HTMLElement | null {
  const el = document.querySelector<HTMLElement>("main.overflow-y-auto, main[class*='overflow-y-auto']");
  return el && el.scrollHeight > el.clientHeight + 40 ? el : null;
}

const ScrollToTopButton = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let container = getScrollContainer();

    const check = () => {
      const winY = window.scrollY || document.documentElement.scrollTop || 0;
      const contY = container?.scrollTop ?? 0;
      setVisible(Math.max(winY, contY) > THRESHOLD);
    };

    const attach = () => {
      container = getScrollContainer();
      container?.addEventListener("scroll", check, { passive: true });
      check();
    };

    window.addEventListener("scroll", check, { passive: true });
    const t = window.setTimeout(attach, 300);
    const poll = window.setInterval(() => {
      const next = getScrollContainer();
      if (next !== container) {
        container?.removeEventListener("scroll", check);
        container = next;
        container?.addEventListener("scroll", check, { passive: true });
      }
      check();
    }, 1500);

    return () => {
      window.removeEventListener("scroll", check);
      container?.removeEventListener("scroll", check);
      window.clearTimeout(t);
      window.clearInterval(poll);
    };
  }, []);

  const toTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    getScrollContainer()?.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <button
      type="button"
      onClick={toTop}
      aria-label="Scroll back to top"
      className={cn(
        "fixed right-3 z-[60] h-11 w-11 rounded-full border border-border bg-card/95 text-foreground shadow-lg backdrop-blur",
        "flex items-center justify-center transition-all duration-200 hover:bg-muted",
        "bottom-20 md:bottom-6",
        visible ? "opacity-100 translate-y-0" : "pointer-events-none opacity-0 translate-y-3",
      )}
    >
      <ArrowUp className="h-5 w-5" />
    </button>
  );
};

export default ScrollToTopButton;
